import React, { useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import ContactFormModal from '../ContactFlow/ContactFormModal';
import NeuroMessage from '../NeuroMessage';
import RemoteSessionModal from '../RemoteSessionModal';
import ShareViaEmailModal from '../ShareViaEmailModal';
import ShareViaSmsModal from '../ShareViaSmsModal';
import { getMockModeAppOptions, setMockModeAppOptions } from '../../services/mock';
import ignite from '../../services/index';
import { buildPageUrl } from '../../utils';
import { ToggleMenu, Modal } from '../../../neuro-ui';
import {useGuideConfig, useUser} from '../../hooks';

const TABS = {
  guideNavigation: 'guide',
  notes: 'notes',
  mock: 'mock',
};

const DevelopmentTool = ({
  enableForAnyEnvironment,
  enableMenu,
  isConversationTestPage,
  onMenuClose,
}) => {
  const { uuid } = useUser();
  const { isMockMode } = useGuideConfig();
  const [showMenu, setShowMenu] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS.guideNavigation);
  const [showMockConfigModal, setShowMockConfigModal] = useState(false);
  const [showRemoteSessionModal, setShowRemoteSessionModal] = useState(false);
  const [showShareViaEmailModal, setShowShareViaEmailModal] = useState(false);
  const [showShareViaSmsModal, setShowShareViaSmsModal] = useState(false);
  const [showContactFormModal, setShowContactFormModal] = useState(false);
  const [mockModeConfig, setMockModeConfig] = useState(
    isMockMode ? getMockModeAppOptions : {}
  );
  const isDevelopmentMode = ignite.getRunMode() === 'development';
  const componentClass = 'developmentMenu';
  const menuIsOpen = showMenu || enableMenu;
  const toggleButtonClass = classnames(`${componentClass}__menuBtn`, {
    menuIsOpen: menuIsOpen,
  });

  const onDevMenuClose = () => {
    setShowMenu(false);
    onMenuClose && onMenuClose();
  };

  /* eslint-disable react/prop-types */
  const DevToolNavigation = ({ id, isDiv, href, onClick, target }) => {
    const Component = isDiv ? 'div' : 'a';
    const componentProps = isDiv
      ? { onClick }
      : {
        href,
        target: target || (isConversationTestPage ? '_blank' : '_self'),
      };
    return (
      <Component className={`${componentClass}__navOption`} {...componentProps}>
        <NeuroMessage id={`devTool.nav.${id}`} tagName="span" />
        <NeuroMessage id={`devTool.nav.${id}.subtitle`} tagName="p" values={{ uuid }}/>
      </Component>
    );
  };

  /* eslint-disable react/prop-types */
  const DevToolTab = ({ id }) => {
    const classes = classnames(`${componentClass}__tab`, {
      active: activeTab === id,
    });
    return (
      <div className={classes} onClick={() => setActiveTab(id)}>
        <NeuroMessage id={`devTool.tab.${id}`} />
      </div>
    );
  };

  /* eslint-disable react/prop-types */
  const DevToolTabContent = ({ id, children }) => {
    const classes = classnames(`${componentClass}__content`, {
      active: activeTab === id,
    });
    return <div className={classes} role="contentinfo" aria-label="Development Tool Content">{children}</div>;
  };

  /* eslint-disable react/prop-types */
  const DevToolNotes = () => {
    const localNotesKey = 'localDevNotes';
    const [localDevNotes, setLocalDevNotes] = useState(
      JSON.parse(window.sessionStorage.getItem(localNotesKey)) || {}
    );
    const [currentNotes, setCurrentNotes] = useState(
      localDevNotes?.[uuid] || ''
    );
    const saveNote = () => {
      const updatedNotes = {
        ...localDevNotes,
        [uuid]: currentNotes,
      };
      window.sessionStorage.setItem(
        localNotesKey,
        JSON.stringify(updatedNotes)
      );
      setLocalDevNotes(updatedNotes);
    };
    const removeNote = (id) => {
      saveNote();
      const updatedNotes = {
        ...localDevNotes,
      };
      delete updatedNotes[id];
      window.sessionStorage.setItem(
        localNotesKey,
        JSON.stringify(updatedNotes)
      );
      setLocalDevNotes(updatedNotes);
    };
    const allNotes = Object.keys(localDevNotes)
      .filter((u) => u !== uuid)
      .map((id) => (
        <section key={id}>
          <div>
            <NeuroMessage id="devTool.notes.uuid" tagName="label" />
            <span>
              <a href={buildPageUrl({ uuid: id, type: 'landing' })}>{id}</a>
            </span>
          </div>
          <div>
            <NeuroMessage id="devTool.notes.note" tagName="label" />
            <span>{localDevNotes[id]}</span>
          </div>
          <i className="fa fa-trash" onClick={() => removeNote(id)} />
        </section>
      ));

    /* eslint-disable react/display-name */
    return (
      <div className={`${componentClass}__notes`}>
        <NeuroMessage
          id="devTool.notes.text"
          tagName="p"
          values={{
            red: (...chunks) => <span className="red-words">{chunks}</span>,
          }}
        />
        <div className={`${componentClass}__currentNotes`}>
          <textarea
            onChange={(e) => setCurrentNotes(e.target.value)}
            value={currentNotes}
          />
          <button onClick={saveNote}>
            <NeuroMessage
              id={
                localDevNotes?.[uuid]
                  ? 'devTool.notes.update'
                  : 'devTool.notes.save'
              }
            />
          </button>
        </div>
        <div className={`${componentClass}__otherNotes`}>
          <NeuroMessage id="devTool.notes.savedNotes" tagName="p" />
          {allNotes}
        </div>
      </div>
    );
  };

  const renderMockModeConfigurationModal = () => {
    const mockModeFields = [
      {
        id: 'isBranchGuide',
        type: 'radio',
        options: [true, false],
        default: false
      },
      {
        id: 'isRemoteSession',
        type: 'radio',
        options: [true, false],
        default: false
      },
    ];

    /* eslint-disable no-prototype-builtins */
    const renderFields = () =>
      mockModeFields.map((field) => {
        const onChange = (e) => {
          const v = e?.target?.value || false;
          setMockModeConfig({
            ...mockModeConfig,
            [field.id]: field.options.hasOwnProperty(v)
              ? field.options[e?.target?.value]
              : undefined,
          });
        };

        return (
          <div key={field.id}>
            <legend>
              <NeuroMessage id={`devTool.mockConfig.${field.id}`} />
            </legend>
            <div className="mockConfigOptions form-inline mb-4">
              {field.options.map((o, i) => {
                return (
                  <div className="form-check mr-3" key={i}>
                    <input
                      className="form-check-input"
                      type={field.type}
                      name={field.id}
                      id={`${field.id}_${o.toString()}`}
                      value={ i }
                      checked={mockModeConfig[field.id] ? mockModeConfig[field.id] === o : field.default === o}
                      onClick={onChange}
                    />
                    <label className="form-check-label" htmlFor={`${field.id}_${o.toString()}`}>
                      <NeuroMessage id={`devTool.mockConfig.${field.id}.${o.toString()}`}/>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        );
      });

    const onApplyMockModeConfig = () => {
      setMockModeAppOptions(mockModeConfig);
      window.location.href = '/guide';
    };

    return (
      <Modal
        show={showMockConfigModal}
        onClose={() => setShowMockConfigModal(false)}
        className={`${componentClass}__mockConfigModal`}
        nodeId="root"
        closeBtn={<i className={'fa fa-times'} />}
        title={<NeuroMessage id="devTool.mockModeConfigModal.title" />}
      >
        <div className="form-group">{renderFields()}</div>
        <div className="mockConfigModalActions">
          <button onClick={onApplyMockModeConfig} className="btn btn-primary">
            <NeuroMessage id="devTool.mockModeConfigModal.saveBtn" />
          </button>
        </div>
      </Modal>
    );
  };

  const remoteSessionModal = (
    <RemoteSessionModal
      className={`${componentClass}__devToolModal`}
      closeBtn={<i className={'fa fa-times'} />}
      isOpen={showRemoteSessionModal}
      onClose={() => setShowRemoteSessionModal(false)}
    />
  );

  const shareViaEmailModal = (
    <ShareViaEmailModal
      className={`${componentClass}__devToolModal`}
      closeBtn={<i className={'fa fa-times'} />}
      isOpen={showShareViaEmailModal}
      onClose={() => setShowShareViaEmailModal(false)}
    />
  );

  const shareViaSmsModal = (
    <ShareViaSmsModal
      className={`${componentClass}__devToolModal`}
      closeBtn={<i className={'fa fa-times'} />}
      isOpen={showShareViaSmsModal}
      onClose={() => setShowShareViaSmsModal(false)}
    />
  );

  const contactFormModal = (
    <ContactFormModal
      className={`${componentClass}__devToolModal socialForm`}
      closeBtn={<i className={'fa fa-times'} />}
      isOpen={showContactFormModal}
      onClose={() => setShowContactFormModal(false)}
    />
  );

  return (
    (enableForAnyEnvironment || isDevelopmentMode) && (
      <>
        {enableMenu === undefined && (
          <button
            className={toggleButtonClass}
            onClick={() => setShowMenu(!showMenu)}
            role="button"
            title="Development Menu"
          >
            <i className="fa fa-cog fa-spin" />
          </button>
        )}

        <ToggleMenu
          className={componentClass}
          animation="slideFromRight"
          isOpen={menuIsOpen}
          onOuterClick={onDevMenuClose}
        >
          <i
            className={`fa fa-times ${componentClass}__closeBtn`}
            onClick={onDevMenuClose}
          />
          <div className={`${componentClass}__header`} role="complementary" aria-label="Development Tool Header">
            <NeuroMessage id="devTool.subtitle" tagName="span" />
            <NeuroMessage id="devTool.title" tagName="h1" />
          </div>

          <div className={`${componentClass}__tabs`} role="navigation" aria-label="Development Tool Navigation Bar">
            <DevToolTab id={TABS.guideNavigation} />
            <DevToolTab id={TABS.notes} />
            {isMockMode && !isConversationTestPage && <DevToolTab id={TABS.mock} />}
          </div>

          <DevToolTabContent id={TABS.guideNavigation}>
            {!isConversationTestPage && (
              <DevToolNavigation
                id="conversationTest"
                href={buildPageUrl({ uuid, type: 'conversationTest' })}
                target="_blank"
              />
            )}
            <DevToolNavigation
              id="summary"
              href={buildPageUrl({ uuid, type: 'summary' })}
            />
            <DevToolNavigation
              id="landing"
              href={buildPageUrl({ uuid, type: 'landingWithUser' })}
            />
            <DevToolNavigation
              id="create"
              href={buildPageUrl({ type: 'newUser' })}
            />
          </DevToolTabContent>

          <DevToolTabContent id={TABS.notes}>
            <DevToolNotes />
          </DevToolTabContent>

          {isMockMode && !isConversationTestPage && (
            <DevToolTabContent id={TABS.mock}>
              <DevToolNavigation
                id="configureMock"
                isDiv={true}
                onClick={() => {
                  setShowMockConfigModal(true);
                  setShowMenu(false);
                }}
              />
              <DevToolNavigation
                id="remoteSessionModal"
                isDiv={true}
                onClick={() => {
                  setShowRemoteSessionModal(true);
                  setShowMenu(false);
                }}
              />
              <DevToolNavigation
                id="shareViaEmailModal"
                isDiv={true}
                onClick={() => {
                  setShowShareViaEmailModal(true);
                  setShowMenu(false);
                }}
              />
              <DevToolNavigation
                id="shareViaSmsModal"
                isDiv={true}
                onClick={() => {
                  setShowShareViaSmsModal(true);
                  setShowMenu(false);
                }}
              />
              <DevToolNavigation
                id="contactFormModal"
                isDiv={true}
                onClick={() => {
                  setShowContactFormModal(true);
                  setShowMenu(false);
                }}
              />
            </DevToolTabContent>
          )}
        </ToggleMenu>

        {showMockConfigModal && renderMockModeConfigurationModal()}
        {showRemoteSessionModal && remoteSessionModal}
        {showShareViaEmailModal && shareViaEmailModal}
        {showShareViaSmsModal && shareViaSmsModal}
        {showContactFormModal && contactFormModal}
      </>
    )
  );
};

DevelopmentTool.propTypes = {
  enableForAnyEnvironment: PropTypes.bool,
  enableMenu: PropTypes.bool,
  isConversationTestPage: PropTypes.bool,
  onMenuClose: PropTypes.func,
};
export default DevelopmentTool;
